import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    MAX_WHATSAPP_REMINDER_TEMPLATE_LENGTH,
    SchedulerSettings,
    SchedulerSettingsUpdate,
} from "../../domain/types";
import {
    fillReminderTemplate,
    REMINDER_PLACEHOLDERS,
} from "../../domain/whatsappReminder";

/**
 * The message the agenda's WhatsApp button writes for the therapist.
 *
 * Unlike the rest of the Scheduler settings this one is saved with a button,
 * not on change: a message is written over several keystrokes, and saving
 * half a sentence on every one of them would be saving drafts nobody asked to
 * keep.
 *
 * Nothing is stored until the therapist writes something of their own, so the
 * default follows the interface's language; saving the default text back, or
 * clearing the box, returns to it.
 */
export default function WhatsAppReminderSettings({
    settings,
    isSaving,
    saveFailed,
    onChange,
}: {
    settings: SchedulerSettings;
    isSaving: boolean;
    saveFailed: boolean;
    onChange: (update: SchedulerSettingsUpdate) => void;
}) {
    const { t } = useTranslation();
    const defaultTemplate = t("scheduler.schedule.whatsappReminderDefaultTemplate");
    const stored = settings.whatsapp_reminder_template || defaultTemplate;

    const [draft, setDraft] = useState(stored);
    const [justSaved, setJustSaved] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // What the server holds wins whenever it changes: after a save, a reset,
    // or a refetch.
    useEffect(() => setDraft(stored), [stored]);

    const tooLong = draft.length > MAX_WHATSAPP_REMINDER_TEMPLATE_LENGTH;
    const unchanged = draft === stored;
    const isCustom = Boolean(settings.whatsapp_reminder_template);

    const save = (value: string) => {
        setJustSaved(true);
        // The default text is not a message of the therapist's own: storing
        // it would pin it in one language and stop it following ours.
        onChange({
            whatsapp_reminder_template:
                value.trim() === defaultTemplate.trim() ? "" : value,
        });
    };

    /** Put the placeholder where the cursor is, not at the end. */
    const insertPlaceholder = (placeholder: string) => {
        const textarea = textareaRef.current;
        const token = `{${placeholder}}`;
        const start = textarea?.selectionStart ?? draft.length;
        const end = textarea?.selectionEnd ?? draft.length;
        setDraft(`${draft.slice(0, start)}${token}${draft.slice(end)}`);
        setJustSaved(false);
        requestAnimationFrame(() => {
            textarea?.focus();
            textarea?.setSelectionRange(start + token.length, start + token.length);
        });
    };

    const preview = fillReminderTemplate(draft, {
        name: t("scheduler.settings.whatsappReminder.previewName"),
        date: t("scheduler.settings.whatsappReminder.previewDate"),
        time: t("scheduler.settings.whatsappReminder.previewTime"),
        link: "https://alchely.com/meet/j/…",
    });

    return (
        <section className="flex max-w-2xl flex-col gap-3">
            <div className="flex flex-col gap-1">
                <h2 className="text-base font-semibold">
                    {t("scheduler.settings.whatsappReminder.title")}
                </h2>
                <p className="text-sm text-content-tertiary">
                    {t("scheduler.settings.whatsappReminder.intro")}
                </p>
            </div>

            <div className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                <label className="flex flex-col gap-1 text-sm">
                    <span className="font-medium">
                        {t("scheduler.settings.whatsappReminder.label")}
                    </span>
                    <textarea
                        ref={textareaRef}
                        value={draft}
                        rows={5}
                        disabled={isSaving}
                        aria-invalid={tooLong || undefined}
                        onChange={(event) => {
                            setDraft(event.target.value);
                            setJustSaved(false);
                        }}
                        className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
                    />
                </label>

                <div className="flex flex-wrap gap-2">
                    {REMINDER_PLACEHOLDERS.map((placeholder) => (
                        <button
                            key={placeholder}
                            type="button"
                            disabled={isSaving}
                            onClick={() => insertPlaceholder(placeholder)}
                            aria-label={t("scheduler.settings.whatsappReminder.insert", {
                                placeholder: `{${placeholder}}`,
                            })}
                            className="rounded-md border border-gray-200 px-2 py-0.5 font-mono text-xs text-brand-600 hover:bg-brand-50 disabled:opacity-50 dark:border-gray-700 dark:text-brand-400 dark:hover:bg-brand-600/10"
                        >
                            {`{${placeholder}}`}
                        </button>
                    ))}
                </div>

                <p className="text-xs text-content-tertiary">
                    {t("scheduler.settings.whatsappReminder.placeholdersHint")}
                </p>

                {tooLong && (
                    <p role="alert" className="text-sm text-red-600">
                        {t("scheduler.settings.whatsappReminder.tooLong", {
                            max: MAX_WHATSAPP_REMINDER_TEMPLATE_LENGTH,
                        })}
                    </p>
                )}

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-tertiary">
                        {t("scheduler.settings.whatsappReminder.preview")}
                    </span>
                    <p className="whitespace-pre-line rounded-lg bg-green-50 px-3 py-2 text-sm text-content-primary dark:bg-green-900/20">
                        {preview}
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        disabled={isSaving || unchanged || tooLong}
                        onClick={() => save(draft)}
                        className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                    >
                        {isSaving
                            ? t("scheduler.settings.saving")
                            : t("scheduler.settings.whatsappReminder.save")}
                    </button>
                    {isCustom && (
                        <button
                            type="button"
                            disabled={isSaving}
                            onClick={() => save("")}
                            className="text-sm text-brand-600 disabled:opacity-50 dark:text-brand-400"
                        >
                            {t("scheduler.settings.whatsappReminder.reset")}
                        </button>
                    )}
                    {justSaved && !isSaving && !saveFailed && unchanged && (
                        <span role="status" className="text-sm text-green-600">
                            {t("scheduler.settings.whatsappReminder.saved")}
                        </span>
                    )}
                    {justSaved && saveFailed && (
                        <span role="alert" className="text-sm text-red-600">
                            {t("scheduler.settings.saveError")}
                        </span>
                    )}
                </div>
            </div>
        </section>
    );
}
